import React from 'react';

import { Typography } from '../Typography/index';
import {
  spec,
  StyledCard,
  CardLeft,
  CardCheckboxBox,
  IconCircle,
  Identity,
  CardRight,
  Divider,
  Details,
  DetailPair,
  DetailLabelRow,
  InfoIconWrapper,
} from './styles';

const CheckIcon = (): React.ReactElement => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable={false}>
    <path
      d="M2 6.5L4.5 9L10 3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Um par "label/valor" exibido à direita do card.
 */
export type SelectableCardDetail = {
  /** rótulo superior (Regular 12px) */
  label: string;
  /** valor inferior (Bold 12px); ReactNode permite formatação/destaque */
  value: React.ReactNode;
  /** quando presente, renderiza o ícone ⓘ ao lado do label (tooltip nativo + aria) */
  info?: string;
};

/**
 * SelectableCard — item de listagem selecionável, genérico e "burro".
 *
 * Sem regra de negócio: recebe um ícone (slot), título, subtítulo e uma lista
 * de detalhes; o estado de seleção é controlado pelo consumidor. Lido do Figma
 * (nodes 9457:3184 / 9457:3322).
 *
 * Acessibilidade: quando `onSelect` é fornecido, o card vira `role="radio"`
 * com `aria-checked`. O `role="radiogroup"` em volta da lista é
 * responsabilidade do CONSUMIDOR (a página/listagem).
 *
 * @example
 * <div role="radiogroup" aria-label="Locais">
 *   <SelectableCard
 *     icon={<LocationPin />}
 *     title="Refeitório Fundos"
 *     subtitle="Rua das Palmeiras, 320 - Bloco D, Unidade 5"
 *     details={[
 *       { label: 'Forma de abastecimento', value: 'Automática', info: 'Como é definido' },
 *       { label: 'Dias de rota', value: 'Seg e Ter' },
 *     ]}
 *     selected={selectedId === '1'}
 *     onSelect={() => setSelectedId('1')}
 *   />
 * </div>
 */
export type SelectableCardProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title' | 'onSelect'
> & {
  /** Ícone à esquerda (slot). Use `currentColor` para herdar a cor do estado. */
  icon?: React.ReactNode;
  /** Título principal (Bold 16px) */
  title: React.ReactNode;
  /** Subtítulo (Regular 12px) */
  subtitle?: React.ReactNode;
  /** Pares de detalhe exibidos à direita */
  details?: SelectableCardDetail[];
  /** Estado de seleção (controlado) */
  selected?: boolean;
  /** Torna o card selecionável (clique + teclado). Ausência = só exibição. */
  onSelect?: () => void;
  /**
   * Semântica de seleção (a11y): `radio` = escolher 1 de N (default);
   * `checkbox` = selecionar um ou mais. Use `checkbox` em listas multi-seleção.
   * @default 'radio'
   */
  selectionRole?: 'radio' | 'checkbox';
  /** Desabilita a seleção */
  disabled?: boolean;
};

const InfoIcon = (): React.ReactElement => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.16663 7.50008H10.8333V5.83341H9.16663V7.50008ZM9.99996 16.6667C6.32496 16.6667 3.33329 13.6751 3.33329 10.0001C3.33329 6.32508 6.32496 3.33341 9.99996 3.33341C13.675 3.33341 16.6666 6.32508 16.6666 10.0001C16.6666 13.6751 13.675 16.6667 9.99996 16.6667ZM9.99996 1.66675C8.90561 1.66675 7.82198 1.8823 6.81093 2.30109C5.79988 2.71987 4.88122 3.3337 4.1074 4.10752C2.5446 5.67033 1.66663 7.78994 1.66663 10.0001C1.66663 12.2102 2.5446 14.3298 4.1074 15.8926C4.88122 16.6665 5.79988 17.2803 6.81093 17.6991C7.82198 18.1179 8.90561 18.3334 9.99996 18.3334C12.2101 18.3334 14.3297 17.4554 15.8925 15.8926C17.4553 14.3298 18.3333 12.2102 18.3333 10.0001C18.3333 8.90573 18.1177 7.8221 17.699 6.81105C17.2802 5.80001 16.6663 4.88135 15.8925 4.10752C15.1187 3.3337 14.2 2.71987 13.189 2.30109C12.1779 1.8823 11.0943 1.66675 9.99996 1.66675ZM9.16663 14.1667H10.8333V9.16675H9.16663V14.1667Z"
      fill="currentColor"
    />
  </svg>
);

export const SelectableCard = ({
  icon,
  title,
  subtitle,
  details,
  selected = false,
  onSelect,
  selectionRole = 'radio',
  disabled = false,
  ...rest
}: SelectableCardProps): React.ReactElement => {
  const interactive = Boolean(onSelect) && !disabled;

  const handleClick = (): void => {
    if (interactive && onSelect) onSelect();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!interactive || !onSelect) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <StyledCard
      $selected={selected}
      $interactive={interactive}
      role={onSelect ? selectionRole : undefined}
      aria-checked={onSelect ? selected : undefined}
      aria-disabled={disabled || undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      {...rest}
    >
      <CardLeft>
        {selectionRole === 'checkbox' && (
          <CardCheckboxBox $selected={selected}>
            {selected && <CheckIcon />}
          </CardCheckboxBox>
        )}
        {icon && <IconCircle $selected={selected}>{icon}</IconCircle>}
        <Identity>
          <Typography as="span" size="lg" weight="extraBold" color={spec.text}>
            {title}
          </Typography>
          {subtitle && (
            <Typography as="span" size="sm" weight="regular" color={spec.text}>
              {subtitle}
            </Typography>
          )}
        </Identity>
      </CardLeft>

      {details && details.length > 0 && (
        <CardRight>
          <Divider aria-hidden="true" />
          <Details>
            {details.map((d, i) => (
              <DetailPair key={`${d.label}-${i}`}>
                <DetailLabelRow>
                  <Typography
                    as="span"
                    size="sm"
                    weight="regular"
                    color={spec.text}
                  >
                    {d.label}
                  </Typography>
                  {d.info && (
                    <InfoIconWrapper
                      role="img"
                      aria-label={d.info}
                      title={d.info}
                    >
                      <InfoIcon />
                    </InfoIconWrapper>
                  )}
                </DetailLabelRow>
                <Typography
                  as="span"
                  size="sm"
                  weight="extraBold"
                  color={spec.text}
                >
                  {d.value}
                </Typography>
              </DetailPair>
            ))}
          </Details>
        </CardRight>
      )}
    </StyledCard>
  );
};

export default SelectableCard;
